import { observer } from 'mobx-react';
import Input from './Input';
import useCityZipAutocomplete from '../../hooks/useCityZipAutocomplete';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * City/Zipcode autocomplete input matching MultiSelect styling
 *
 * TODO: Add keyboard navigation (ArrowUp/Down, Home/End) through suggestions
 * TODO: Improve accessibility: ARIA roles/activedescendant announcements
 */
const CityZipAutocompleteInput = observer(
  ({
    field,
    value,
    setFieldValue,
    countries = [],
    disabled = false,
    messages = [],
    showOnSubmitErrorState = false,
    inputWrap = { enable: true, className: 'col' }
  }) => {
    // FIXME: Temporarily restricted to Germany-only due to backend limitation
    // Original logic (to be restored when backend supports multi-country geo search):
    // const isGermanySelected = useMemo(() => {
    //   if (!Array.isArray(countries)) return false;
    //   return countries.some((c) => {
    //     const s = `${c}`.toLowerCase().trim();
    //     return ['de', 'deu', 'germany', 'deutschland'].includes(s);
    //   });
    // }, [countries]);
    
    const isGermanyOnlySelected = useMemo(() => {
      // Only enable when Germany is the sole selection (backend limitation)
      if (!Array.isArray(countries) || countries.length !== 1) return false;
      const country = `${countries[0]}`.toLowerCase().trim();
      return ['de', 'deu', 'germany', 'deutschland'].includes(country);
    }, [countries]);
    const enabled = !disabled && isGermanyOnlySelected;
    const { query, setQuery, suggestions, loading, error, selectLabel, selectValue } =
      useCityZipAutocomplete({
        enabled,
        initialQuery: value || ''
      });
    const dropdownRef = useRef(null);
    const [focused, setFocused] = useState(false);
    useEffect(() => {
      // FIXME: Change back to isGermanySelected when backend supports multi-country geo search
      if (!isGermanyOnlySelected) {
        setFieldValue(field, ''); // reset stored value
      }
    }, [isGermanyOnlySelected, setFieldValue, field]);
    const onAfterValueChange = (v) => {
      setQuery(v);
    };

    const onSelect = (item) => {
      // Option B: if a postcode is available, prefer using it as the stored value
      const val = item?.type === 'postal' && item?.value ? item.value : selectValue(item);
      setFieldValue(field, val);
      setQuery(val);
      setFocused(false);
      setTimeout(() => {
        try {
          document.getElementById(field.ID)?.blur?.();
        } catch {}
      }, 0);
    };

    const hasQuery = (query || '').trim().length >= 2;
    const shouldShowDropdown =
      focused && enabled && (loading || error || (hasQuery && suggestions.length));

    const suggestionList = shouldShowDropdown ? (
      <div
        className="optionListContainer displayBlock"
        ref={dropdownRef}
        style={{ position: 'absolute', left: 0, right: 0, zIndex: 10 }}>
        <ul className="optionContainer">
          {loading && <li className="option disabled-option">Loading…</li>}
          {!loading && !suggestions.length && hasQuery && (
            <li className="option disabled-option">No results</li>
          )}
          {suggestions.map((s, idx) => (
            <li key={`${s.type || ''}-${s.value || s.city || ''}-${idx}`} className="option">
              <button
                type="button"
                className="option-btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(s);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(s);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelect(s);
                  }
                }}>
                {selectLabel(s)}
              </button>
            </li>
          ))}
          {!!error && <li className="option disabled-option">{error}</li>}
        </ul>
      </div>
    ) : null;

    return (
      <div className="col">
        <div
          className={`form-group select-form-group${focused ? ' focused' : ''}`}
          style={{ position: 'relative', width: '100%' }}>
          <div style={{ position: 'relative', width: '100%' }}>
            <Input
              field={field}
              value={value}
              setFieldValue={setFieldValue}
              messages={messages}
              showOnSubmitErrorState={showOnSubmitErrorState}
              inputWrap={inputWrap}
              disabled={!enabled}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 100)}
              onAfterValueChange={onAfterValueChange}
            />
            {suggestionList}
          </div>
        </div>
      </div>
    );
  }
);

export default CityZipAutocompleteInput;
